import { css } from 'styled-components';

import { CommonTheme, ManualCSSTheme } from 'app/shared/theme';
import { ImageSourceMap } from 'app/shared/typings';

export interface WithBackgroundProps {
  backgroundColor?: string;
  backdropFilter?: string;
  backgroundPosition?: string;
  backgroundGrayscale?: boolean;
  backgroundGradient?: string;
  hoverBackgroundGradient?: string;
  backgroundImage?: string | string[] | ImageSourceMap;
  hoverBackgroundColor?: string;
}

interface WithBackgroundCSSProps extends WithBackgroundProps {
  theme: CommonTheme | ManualCSSTheme;
}

interface BackgroundImageProps {
  backgroundImage: string | string[] | ImageSourceMap;
  theme: CommonTheme | ManualCSSTheme;
}
const generateBackgroundImageCSS = ({
  theme,
  backgroundImage,
}: BackgroundImageProps) => {
  let styles: any[] = [
    'background-repeat: no-repeat;',
    'background-size: cover;',
    'background-blend-mode: multiply;',
  ];

  if (typeof backgroundImage === 'string') {
    styles.push(
      `background-image: ${backgroundImage.includes('url(') ? backgroundImage : `url(${backgroundImage})`};`
    );
  } else if (Array.isArray(backgroundImage)) {
    styles.push('background-position: center;');

    const breakPointKeys = Object.keys(theme.breakPoints);

    backgroundImage.forEach((image: string, index: number) => {
      const breakpointKey = breakPointKeys[index];
      const mediaQuery = theme.utils.getMediaQuery(breakpointKey);

      styles.push(mediaQuery`background-image: url(${image});`);
    });
  } else if (typeof backgroundImage === 'object') {
    styles.push('background-position: center;');

    const mapping: { [key: string]: keyof ImageSourceMap } = {
      xs: 'srcSmall',
      sm: 'srcSmall',
      md: 'srcMedium',
      lg: 'srcLarge',
      xl: 'srcLarge',
      mobile: 'srcMobile',
      tablet: 'srcTablet',
      smallDesktop: 'srcSmallDesktop',
      largeDesktop: 'srcLargeDesktop',
    };

    Object.keys(theme.breakPoints).forEach((breakPointKey) => {
      const mediaQuery = theme.utils.getMediaQuery(breakPointKey);
      const srcKey = mapping[breakPointKey];
      const url = backgroundImage[srcKey]
        ? backgroundImage[srcKey]
        : backgroundImage.src;

      styles.push(mediaQuery`background-image: url(${url});`);
    });
  }

  return styles;
};

export const withBackgroundCSS = ({
  theme,
  backgroundColor,
  backdropFilter,
  backgroundGrayscale,
  backgroundImage,
  backgroundGradient,
  hoverBackgroundGradient,
  backgroundPosition,
  hoverBackgroundColor,
}: WithBackgroundCSSProps) => css`
  ${backgroundColor && `background-color: ${backgroundColor};`}
  ${backdropFilter && `backdrop-filter: ${backdropFilter};`}
  ${backgroundGrayscale && 'filter: grayscale(100%);'}
  ${
    hoverBackgroundColor &&
    `&:hover {
      background-color: ${hoverBackgroundColor};
    }`
  }
  ${
    hoverBackgroundGradient &&
    `&:hover {
      background: ${hoverBackgroundGradient};
    }`
  }
  ${backgroundImage && generateBackgroundImageCSS({ theme, backgroundImage })}
  ${backgroundGradient && `background: ${backgroundGradient};`}
  ${backgroundPosition && `background-position: ${backgroundPosition};`}
`;
